import { computed, signal } from '@angular/core';
import * as _ from 'lodash';
import { HisUidService } from '../../core/service/base/his-uid.service';
import { HisQueryParamUtils } from '../utils/his-query-param-utils';
import { HisWidgetUtils } from '../utils/his-widget-utils';

export abstract class HisWidgetBase<T> {
    group: string;
    kind: string;
    private _value: T;
    originalValue: T;
    id: string;
    uid: string;
    label: string;
    helptext: string;
    order: number;
    errors: string[];
    quickSearch: boolean;

    /** Reflects whether this widget is defined by a search set. In this case, it must be displayed, regardless if its a quicksearch criterion or not. */
    isActiveBySearchSet = signal( false );
    /** Reflects whether this widgets value is preset. In this case, it must be displayed, regardless if its a quicksearch criterion or not. */
    isActiveByPreset = signal( false );
    /** Defines whether this widget should be displyed in the search mask. */
    shouldDisplay = computed( () => this.quickSearch || this.isActiveBySearchSet() || this.isActiveByPreset() );

    // Pflichtfeld? Aus [https://wiki.his.de/mediawiki/index.php/Technische_Dokumentation_Generische_Suche-HISinOne]:
    // "Kann nur in Kombination mit searchEntity+searchConfigFile oder treeName+selectFromTree
    // verwendet werden, um mindestens eine Unterelement-Auswahl zu erzwingen.
    // Alle "normalen" Eingabefelder können/sollen über das DataDictionary auf required gesetzt werden!"
    // In der Praxis wird dieses Attribut auch bei anderen Feldern verwendet. Default = false.

    required: boolean;

    // inputType is used for frontend validations.
    // Possible values: null, 'intnumber'.
    inputType?: string;

    constructor( options: any ) {
        this.group = options.group;
        this.kind = options.kind || 'text';
        this.value = options.value;
        this.originalValue = options.value;
        this.id = options.id;
        // The uid needs a random part, because searchCriterion.path is not unique
        // if the same search criterion is included twice in a page
        // (e.g. when we have a sub-search for project lead and another one for project members)
        this.uid = HisUidService.get( options.id + '.' );

        this.label = options.label || '';
        this.helptext = options.helptext;
        this.order = ( options.order === undefined ) ? 0 : options.order;
        this.errors = [];
        this.quickSearch = ( _.isNil( options.quickSearch ) ) ? true : !!options.quickSearch;
        this.required = options.required || false;
        this.inputType = options.inputType;
    }

    /**
     * Default implementation for inputs, calendar, checkbox.
     * 
     * If no presets are given, nothing is done to prevent overriding searchConfig presets.
     */
    public presetInputFields( presets: any ) {
        if ( !presets ) {
            return;
        }

        if ( HisQueryParamUtils.isFlatObjectFormat( presets ) ) {
            this.setPreset( HisWidgetUtils.getPresetForWidget( this, presets ) );
            return;
        }

        // presets with key value fromat
        let presetValue = presets[this.id];
        this.setPreset( presetValue );
    }

    /**
     * Presets the value and originalValue of this widget.
     * @param presetValue - the value to set
     */
    private setPreset( presetValue: any ) {
        if ( !presetValue ) {
            this.value = undefined;
            this.originalValue = undefined;
            this.isActiveByPreset.set( false );
            return;
        }
        console.log( 'presetting input field ' + this.id + ' =', presetValue );
        this.value = presetValue;
        this.originalValue = presetValue;
        this.isActiveByPreset.set( true );
        return;
    }

    /**
     * Informs whether the widget's value has been modified compared to its originalValue.
     */
    public abstract isWidgetModified(): boolean;

    //needed to override value handling in subclasses (example: HisOptionWidget)
    get value(): T {
        return this._value;
    }
    set value( value: T ) {
        this._value = value;
    }
}
